import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FormattedMessage} from "react-intl";
import {IUserInfo, UserTypeEnum} from "../../model/UserInfo.model";
import {getTranslation} from "../../utils/translationUtil";
import {useClickOutside} from "../../utils/UseClickOutside";
import {RaisedButton} from "../common/RaisedButton";
import {assignButtonStyle} from "../common/buttonStyles";

const MAX_SEARCH_RESULTS = 10;
const MIN_SEARCH_CHARS = 3;
const LISTBOX_ID = "user-search-dropdown__search-input-listbox";

export interface IUserSearchDropdownProps {
    allUsers: IUserInfo[];
    planId: number;
    planName: string;
    onAssignUser?: (user: IUserInfo) => void;
}

export const UserSearchDropdown: React.FunctionComponent<IUserSearchDropdownProps> = (props) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [showResults, setShowResults] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);

    const {targetRef: searchAreaRef} = useClickOutside(() => {
        setShowResults(false);
        setActiveIndex(-1);
    });

    const availableUsers = useMemo(() => {
        return props.allUsers.filter((u) => !u.planIds.includes(props.planId));
    }, [props.allUsers, props.planId]);

    const allSearchResults = useMemo(() => {
        if (searchQuery.length < MIN_SEARCH_CHARS) return [];
        const q = searchQuery.toLowerCase();
        return availableUsers.filter((u) =>
            u.firstName.toLowerCase().includes(q)
            || u.lastName.toLowerCase().includes(q)
            || u.email.toLowerCase().includes(q)
            || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q),
        );
    }, [availableUsers, searchQuery]);

    const searchResults = allSearchResults.slice(0, MAX_SEARCH_RESULTS);
    const moreResultsCount = allSearchResults.length - searchResults.length;
    const isDropdownOpen = showResults && searchQuery.length >= MIN_SEARCH_CHARS;

    const clearSearch = useCallback(() => {
        setSearchQuery("");
        setShowResults(false);
        setActiveIndex(-1);
        if (inputRef.current) inputRef.current.focus();
    }, [inputRef]);

    const handleSelect = useCallback((user: IUserInfo) => {
        if (!props.onAssignUser) return;
        props.onAssignUser(user);
        clearSearch();
    }, [props.onAssignUser, clearSearch]);

    const formatRole = (userType: UserTypeEnum): string => {
        return userType.charAt(0) + userType.slice(1).toLowerCase();
    };

    const highlightMatch = (text: string, query: string): React.ReactNode => {
        if (!query || query.length < MIN_SEARCH_CHARS) return text;
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));
        return <>
            {parts.map((part, i) =>
                i % 2 === 1
                    ? <span key={i} className="user-search-dropdown__highlight">{part}</span>
                    : <React.Fragment key={i}>{part}</React.Fragment>,
            )}
        </>;
    };

    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isDropdownOpen) return;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setActiveIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : 0));
                break;
            case "ArrowUp":
                e.preventDefault();
                setActiveIndex((prev) => (prev > 0 ? prev - 1 : searchResults.length - 1));
                break;
            case "Enter":
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < searchResults.length && props.onAssignUser) {
                    handleSelect(searchResults[activeIndex]);
                }
                break;
            case "Escape":
                e.preventDefault();
                setShowResults(false);
                setActiveIndex(-1);
                break;
            default:
                break;
        }
    }, [isDropdownOpen, searchResults, activeIndex, handleSelect, props.onAssignUser]);

    useEffect(() => { setActiveIndex(-1); }, [searchQuery]);

    const getOptionId = (index: number): string => `plan-users-option-${index}`;

    const renderSearchField = () => {
        return <>
            <label className="user-search-dropdown__search-label" htmlFor="user-search-dropdown__search-input">
                <FormattedMessage id="admin.plan-users.add-label" defaultMessage="Add a user to this plan" />
                {props.planName && ` - ${props.planName}`}
            </label>
            <div className="user-search-dropdown__search-wrapper">
                    <span className="user-search-dropdown__search-icon" aria-hidden="true">
                        <i className="fa-solid fa-magnifying-glass" />
                    </span>
                <input
                    type="text"
                    id="user-search-dropdown__search-input"
                    ref={inputRef}
                    className="user-search-dropdown__search-input"
                    placeholder={getTranslation("admin.plan-users.search-placeholder", "Search by name or email address...")}
                    autoComplete="off"
                    role="combobox"
                    aria-label={getTranslation("admin.plan-users.search-aria", "Search for users to add to this plan")}
                    aria-expanded={isDropdownOpen}
                    aria-controls={isDropdownOpen ? LISTBOX_ID : undefined}
                    aria-autocomplete="list"
                    aria-activedescendant={isDropdownOpen && activeIndex >= 0 ? getOptionId(activeIndex) : undefined}
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setShowResults(true); }}
                    onFocus={() => setShowResults(true)}
                    onKeyDown={handleSearchKeyDown}
                    data-testid="user-search-dropdown__search-input"
                />
            </div>
        </>;
    };

    const renderNoResultsMessage = () => {
        return <div className="user-search-dropdown__no-results">
            <FormattedMessage id="admin.plan-users.no-results" defaultMessage="No matching users found" />
        </div>;
    };

    const renderAssignButton = (user: IUserInfo) => {
        return <RaisedButton
            className="user-search-dropdown__btn-assign"
            style={assignButtonStyle}
            primary={true}
            onClick={() => handleSelect(user)}
            aria-label={`${getTranslation("admin.plan-users.assign-aria", "Assign")} ${user.firstName} ${user.lastName} ${getTranslation("admin.plan-users.to-plan", "to plan")}`}
            tabIndex={-1}
            data-testid={`user-search-dropdown__btn-assign-${user.arbtSecurityId}`}
        >
            + <FormattedMessage id="admin.plan-users.assign" defaultMessage="Assign" />
        </RaisedButton>;
    };

    const renderUsersWithAssignButton = (user: IUserInfo, idx: number) => {
        return <div key={user.arbtSecurityId} id={getOptionId(idx)}
                    className={`user-search-dropdown__result-item${idx === activeIndex ? " user-search-dropdown__result-item--active" : ""}`}
                    role="option" aria-selected={idx === activeIndex}
                    data-testid={`user-search-dropdown__result-item-${user.arbtSecurityId}`}>
            <div className="user-search-dropdown__result-info">
                <div className="user-search-dropdown__result-name">
                    {highlightMatch(`${user.firstName} ${user.lastName}`, searchQuery)}
                </div>
                <div className="user-search-dropdown__result-email">
                    {highlightMatch(user.email, searchQuery)}
                </div>
                <div className="user-search-dropdown__result-role">{formatRole(user.userType)}</div>
            </div>
            {props.onAssignUser && renderAssignButton(user)}
        </div>;
    };

    const renderSearchedUsersInfo = () => {
        return <div>
            {searchResults.map((user, idx) => (renderUsersWithAssignButton(user, idx)))}
            {moreResultsCount > 0 && renderMoreCountMessage()}
        </div>;
    };

    const renderMoreCountMessage = () => {
        return <div className="user-search-dropdown__more-results" data-testid="user-search-dropdown__more-results">
            {moreResultsCount}{" "}
            <FormattedMessage id="admin.plan-users.more-results"
                              defaultMessage="more results — refine your search" />
        </div>;
    };

    const renderSearchResults = () => {
        return <div className="user-search-dropdown__search-results" id={LISTBOX_ID} role="listbox"
                    aria-label={getTranslation("admin.plan-users.results-aria", "Search results")}
                    data-testid="user-search-dropdown__search-results">
            {searchResults.length === 0 ? renderNoResultsMessage() : renderSearchedUsersInfo()}
        </div>;
    };

    return (
        <div className="user-search-dropdown__container" ref={searchAreaRef} data-testid="user-search-dropdown__container">
            {renderSearchField()}
            {isDropdownOpen && renderSearchResults()}
        </div>
    );
};
